import type { EntityDetailRendererFactory } from './types';

/**
 * Registry for entity-specific detail renderers.
 * Allows customization of entity detail screens without modifying core EntityDetail component.
 * 
 * Usage:
 * 1. Create a renderer factory in components/entities/{entity-type}/
 * 2. Register it: registerEntityDetailRenderer('person', createPersonDetailRenderer)
 * 3. The EntityDetail component will automatically use it
 */
class EntityDetailRendererRegistry {
  private renderers = new Map<string, EntityDetailRendererFactory>();

  /**
   * Register a custom detail renderer for an entity type
   */
  register(entityType: string, factory: EntityDetailRendererFactory): void {
    this.renderers.set(entityType, factory);
  }

  /**
   * Get the renderer factory for an entity type
   */
  get(entityType: string): EntityDetailRendererFactory | undefined {
    return this.renderers.get(entityType);
  }

  /**
   * Check if a custom renderer is registered for an entity type
   */
  has(entityType: string): boolean {
    return this.renderers.has(entityType);
  }

  /**
   * Bulk register multiple renderers
   */
  registerAll(renderers: Record<string, EntityDetailRendererFactory>): void {
    Object.entries(renderers).forEach(([entityType, factory]) => {
      this.register(entityType, factory);
    });
  }
}

// Singleton instance
export const entityDetailRendererRegistry = new EntityDetailRendererRegistry();

/**
 * Convenience function for registering a renderer
 */
export const registerEntityDetailRenderer = (
  entityType: string,
  factory: EntityDetailRendererFactory,
): void => {
  entityDetailRendererRegistry.register(entityType, factory);
};
